import React, {useMemo} from "react";
import {BlockComponentContextProvider, FullScreenModal} from "@reside/ui";

import {AdmissionStatus} from "../../../services/AdmissionsService";
import {
  CompletionCheck,
  Draft,
  DraftActions,
  UIActions,
  UIState,
} from "../model";
import {SlideShow} from "../slideshow";
import {HelpControlModal, HelpControlReminder} from "../help-control";
import {PauseOverlayModal} from "../pause-overlay-modal";
import {SignAdmissionOverlay} from "../sign-admission";
import {AdmissionContextProvider} from "../AdmissionContext";

import {ConnectedTableOfContentsProvider} from "../ToCContext";
import {admissionBlockComponents} from "../AdmissionBlockComponentsContextProvider";
import {useProcessedFileAnswers} from "../../admin-admission-explorer/hooks/useProcessedFileAnswers";

type Props = Readonly<{
  draft: Draft;
  draftActions: DraftActions;
  uiActions: UIActions;
  uiState: UIState;
  completionChecklist: CompletionCheck[];
  logoutSession: () => void;
  loadingPatchAdmission: boolean;
  hasClearHiddenFieldsFlag: boolean;
}>;

export const AdmissionExplorer = ({
  draft,
  draftActions,
  uiState,
  uiActions,
  completionChecklist,
  logoutSession,
  loadingPatchAdmission,
  hasClearHiddenFieldsFlag,
}: Props) => {
  const {draft: processedDraft, restorePersistedFileAnswers} =
    useProcessedFileAnswers(draft);
  const draftWithProcessedAnswers = processedDraft || draft;

  // The pre-signed file URLs above are display-only. Restore the durable
  // ``/api/files/<uuid>`` value before any answers are persisted, so an
  // expiring URL is never saved into the admission (DEV-5539).
  const safeDraftActions = useMemo<DraftActions>(
    () => ({
      ...draftActions,
      setAnswers: answers =>
        draftActions.setAnswers(restorePersistedFileAnswers(answers)),
      setAfterAdmissionAnswers: answers =>
        draftActions.setAfterAdmissionAnswers(
          restorePersistedFileAnswers(answers),
        ),
      slideOnSubmit: data =>
        draftActions.slideOnSubmit({
          ...data,
          values: restorePersistedFileAnswers(data.values),
        }),
    }),
    [draftActions, restorePersistedFileAnswers],
  );

  return (
    <AdmissionContextProvider
      {...{
        draft: draftWithProcessedAnswers,
        draftActions: safeDraftActions,
        uiState,
        uiActions,
        completionChecklist,
        logoutSession,
        loadingPatchAdmission,
        hasClearHiddenFieldsFlag,
      }}
    >
      <ConnectedTableOfContentsProvider admissionId={draft.instance.id}>
        <BlockComponentContextProvider value={admissionBlockComponents}>
          <SlideShow />
          <PauseOverlayModal
            isOpen={uiState.paused}
            onLogout={logoutSession}
            onClose={uiActions.resume}
            textSize={uiState.textSize}
          />

          <FullScreenModal
            noCloseButton
            isOpen={uiState.signModalIsOpen}
            onRequestClose={uiActions.closeSignModal}
          >
            {() => (
              <SignAdmissionOverlay
                onRequestClose={uiActions.closeSignModal}
                {...{
                  draft: draftWithProcessedAnswers,
                  draftActions: safeDraftActions,
                  uiState,
                  uiActions,
                  logoutSession,
                }}
              />
            )}
          </FullScreenModal>
          <HelpControlModal />
          <HelpControlReminder
            disabled={draft.instance.status === AdmissionStatus.SIGNED}
          />
        </BlockComponentContextProvider>
      </ConnectedTableOfContentsProvider>
    </AdmissionContextProvider>
  );
};
